/**
 * Client transport + codec glue. (§2.8, §15.4)
 *
 * ── Why there is a Transport interface at all ───────────────────────────────
 *
 * §1.2 specifies WebRTC DataChannels via geckos.io, and warns — correctly — that
 * WebSockets are TCP, so a lost packet head-of-line-blocks every packet behind
 * it. The MVP nevertheless ships plain WebSocket (geckos pulls in
 * node-datachannel, an unwanted native build; see pnpm-workspace.yaml, which
 * pins `node-datachannel: false`). That deviation is deliberate and temporary.
 *
 * So the transport is exactly one narrow interface — bytes in, bytes out, four
 * lifecycle callbacks. Nothing above this file knows what carries the bytes.
 * Dropping geckos in later is `createGeckosTransport(url): Transport` plus one
 * argument at the call site; room, prediction, interpolation and render code do
 * not change. Do NOT let a WebSocket type escape this module — the moment
 * `ws.readyState` appears in game code, that swap stops being a one-liner.
 *
 * ── What the head-of-line blocking actually costs us today ──────────────────
 *
 * On TCP a dropped snapshot delays the ones behind it rather than vanishing.
 * The interpolation buffer (§2.5) absorbs a delay of up to
 * INTERPOLATION_DELAY_MS, then extrapolates, then freezes — so a retransmit
 * inside ~100ms is invisible and one beyond it degrades gracefully instead of
 * corrupting state. The delta chain is also *helped* by TCP: ordering is
 * guaranteed, so the server can delta against the last frame it SENT and this
 * client is guaranteed to hold it. `baselineTick` is still checked below,
 * because that assumption dies the day the transport becomes unreliable.
 *
 * ── Snapshot baselines ─────────────────────────────────────────────────────
 *
 * snapshot.ts's header is the authority: a delta is decoded against the frame
 * the client currently holds. We keep exactly two frames and SWAP them per
 * packet rather than copying — `decodeSnapshot` already writes a complete frame
 * into `out`, so the old baseline becomes the next decode target. Consumers get
 * a borrowed reference valid only for the duration of the callback (see
 * `onSnapshot`).
 */

import {
  type BuildFingerprint,
  BitReader,
  BitWriter,
  type FingerprintMismatchReason,
  MessageType,
  NO_INPUT_SEQ,
  type RefSignalFrame,
  type RejectReason,
  type SnapshotFrame,
  type SnapshotHeader,
  type WelcomePayload,
  beginFrame,
  buildFingerprint,
  classifyFingerprintMismatch,
  createRefSignalFrame,
  createSnapshotFrame,
  decodeControlAssign,
  decodeRefSignal,
  decodeSnapshot,
  decodeWelcome,
  encodeClientHello,
  encodeInputBatch,
  framePayload,
  mismatchMessage,
  peekPacketType,
} from '@fg/shared/net';
import type { InputCommand } from '@fg/shared/sim';

// ---------------------------------------------------------------------------
// Transport — the seam geckos.io drops into. (§1.2)
// ---------------------------------------------------------------------------

/**
 * A bidirectional binary channel. Deliberately minimal: no readyState enum, no
 * reconnect policy, no framing concept. Anything richer would leak a specific
 * transport's model into game code and defeat the purpose.
 */
export interface Transport {
  /** True once the channel can carry bytes. Sends before this are dropped. */
  readonly open: boolean;
  /** Fire-and-forget. Implementations MUST NOT throw on a closed channel. */
  send(bytes: Uint8Array): void;
  close(): void;
  onOpen: (() => void) | null;
  onMessage: ((bytes: Uint8Array) => void) | null;
  /** `reason` is human-readable only — never branch on its text. */
  onClose: ((reason: string) => void) | null;
  onError: ((err: Error) => void) | null;
}

/** Constructs a transport for a URL. The one thing to swap for geckos.io. */
export type TransportFactory = (url: string) => Transport;

/**
 * Browser WebSocket transport, binary. `binaryType = 'arraybuffer'` matters:
 * the default is Blob, which would force an async read per packet and put a
 * promise in the 60Hz receive path.
 */
export function createWebSocketTransport(url: string): Transport {
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';

  const t: Transport = {
    get open(): boolean {
      return ws.readyState === WebSocket.OPEN;
    },
    send(bytes: Uint8Array): void {
      if (ws.readyState !== WebSocket.OPEN) return;
      // Copy via slice(): `bytes` is almost always a subarray view onto a
      // pooled BitWriter buffer that we overwrite on the very next packet. The
      // browser may serialize the send asynchronously, so handing it the live
      // view would ship whatever the buffer holds by then — a corruption bug
      // that only appears under load, which is the worst kind to find later.
      ws.send(bytes.slice().buffer);
    },
    close(): void {
      // 1000 = normal closure. Guard the state: close() on a CONNECTING socket
      // throws in some browsers.
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'client closed');
      }
    },
    onOpen: null,
    onMessage: null,
    onClose: null,
    onError: null,
  };

  ws.onopen = (): void => t.onOpen?.();
  ws.onmessage = (ev: MessageEvent<unknown>): void => {
    const data = ev.data;
    if (data instanceof ArrayBuffer) t.onMessage?.(new Uint8Array(data));
    // A text frame is a protocol violation (§15.4 — no JSON on the wire). Drop
    // it silently rather than trying to interpret it.
  };
  ws.onclose = (ev: CloseEvent): void => t.onClose?.(ev.reason || `code ${ev.code}`);
  ws.onerror = (): void => {
    // The browser deliberately withholds WebSocket error detail (it leaks
    // cross-origin information), so there is nothing more specific to report.
    t.onError?.(new Error('websocket error'));
  };

  return t;
}

// ---------------------------------------------------------------------------
// Ping / Pong
// ---------------------------------------------------------------------------

/**
 * ⚠ CONTRACT NOTE: protocol.ts reserves Ping (0x14) / Pong (0x13) and documents
 * them as "echoes the client's nonce", but ships no codec for either — unlike
 * every other frame. This is that codec, and the server must match it:
 *
 *     Ping: [u8 0x14][u32 nonce]      Pong: [u8 0x13][u32 nonce]
 *
 * Nothing depends on it. RTT is a HUD readout; the interpolation clock derives
 * server time from snapshot arrivals (see interpolation.ts ServerClock), not
 * from RTT. If the server never answers a Ping, `rttMs` simply stays -1 and the
 * game plays exactly the same. Keep it that way — the moment RTT becomes
 * load-bearing, this invented format becomes a silent desync surface.
 */
const PING_NONCE_BITS = 32;

// ---------------------------------------------------------------------------
// GameConnection
// ---------------------------------------------------------------------------

export interface GameConnectionEvents {
  /** Handshake accepted. A full snapshot follows immediately (welcome.ts). */
  onWelcome?: (payload: WelcomePayload) => void;
  /**
   * A decoded snapshot. `frame` is BORROWED — it is the connection's live
   * baseline and is reused on the next packet. Copy anything you keep
   * (copySnapshotFrame) before returning.
   */
  onSnapshot?: (frame: SnapshotFrame, header: SnapshotHeader) => void;
  onReject?: (reason: RejectReason) => void;
  onClose?: (reason: string) => void;
  onError?: (err: Error) => void;
  /**
   * The referee whistled a restart or showed a card (§5.6). PRESENTATION ONLY:
   * drive a whistle sound, a banner and the 3D referee's cue off it and nothing
   * else — the sim is unaffected and a dropped frame changes no outcome.
   *
   * `signal` is BORROWED, like `onSnapshot`'s frame: it is the connection's
   * reusable decode target and is overwritten by the next such packet. Read what
   * you need before returning.
   */
  onRefereeSignal?: (signal: RefSignalFrame) => void;
  /** Server spoke a protocol we don't — §12.6 says force a refresh, never play on. */
  onProtocolMismatch?: (serverVersion: number, clientVersion: number) => void;
  /**
   * The server is running a gameplay-critical DIFFERENT build (§12.6, §15.2):
   * protocol, sim behaviour, or a gameplay constant differs — or, in production,
   * the server sent no fingerprint at all. The connection has already closed; the
   * caller must surface this to the user (a silent log is not enough) and force a
   * refresh. `message` is ready to display; `reason` is for branching/telemetry.
   */
  onVersionMismatch?: (detail: {
    reason: FingerprintMismatchReason;
    message: string;
    local: BuildFingerprint;
    server: BuildFingerprint;
  }) => void;
}

export interface GameConnectionOptions {
  /** ws:// or wss:// game-server URL, from the lobby handoff. */
  url: string;
  /** Signed join token minted by the lobby (§9.4). */
  token: string;
  events?: GameConnectionEvents;
  /** Swap point for geckos.io. Defaults to the WebSocket transport. */
  transportFactory?: TransportFactory;
  /**
   * Reject a server that sends NO build fingerprint (§12.6). Defaults to
   * production only: a dev stack may legitimately be mid-edit, but a shipped
   * client must never play against a server it cannot identify. Injectable for
   * tests; otherwise derived from the Vite build mode.
   */
  requireFingerprint?: boolean;
}

/**
 * Owns the wire: framing, the handshake, the snapshot baseline chain, and RTT.
 * Knows nothing about rendering, prediction or the sim — it hands decoded frames
 * up and takes InputCommands down.
 */
export class GameConnection {
  private readonly url: string;
  private readonly token: string;
  private readonly events: GameConnectionEvents;
  private readonly factory: TransportFactory;
  /** Production requires a server fingerprint; dev tolerates its absence. */
  private readonly requireFingerprint: boolean;
  private transport: Transport | null = null;

  // Zero-allocation codec scratch (§15.7): one writer, one reader, two frames.
  private readonly writer = new BitWriter();
  private readonly reader = new BitReader(new Uint8Array(1));
  /** What this client currently holds — the frame a delta is decoded against. */
  private baseline: SnapshotFrame = createSnapshotFrame();
  /** Decode target; swapped with `baseline` on every accepted snapshot. */
  private pending: SnapshotFrame = createSnapshotFrame();
  /** §5.6 referee-signal decode target. One struct, reused — these arrive a few
   *  times a minute and the callback consumes them synchronously. */
  private readonly refSignal: RefSignalFrame = createRefSignalFrame();

  private welcomed = false;
  private pingNonce = 1;
  private pingSentAtMs = 0;
  private pingInFlight = 0;
  /** Smoothed round-trip time, or -1 until the first Pong. */
  private rtt = -1;

  /** Last input seq the server confirmed processing (§2.4). */
  lastProcessedInputSeq = NO_INPUT_SEQ;

  /**
   * The server reassigned which PlayerId this connection drives (§4.8): a switch
   * it honoured, an auto-switch, or a drop-in. Set by `wireSwitching`
   * (game/playerSwitch.ts) rather than passed at construction, because prediction
   * and the switcher do not exist until the Welcome arrives. Authoritative — this
   * is what actually moves control, ahead of any optimistic client guess.
   */
  onControlAssign: ((playerId: number) => void) | null = null;

  constructor(opts: GameConnectionOptions) {
    this.url = opts.url;
    this.token = opts.token;
    this.events = opts.events ?? {};
    this.factory = opts.transportFactory ?? createWebSocketTransport;
    // Default: enforce a fingerprint in a production build only. import.meta.env
    // is Vite's — PROD is true in a shipped bundle, false under `vite dev`.
    this.requireFingerprint = opts.requireFingerprint ?? Boolean(import.meta.env.PROD);
  }

  get open(): boolean {
    return this.transport?.open ?? false;
  }

  /** Smoothed RTT in ms, or -1 if the server has never answered a Ping. */
  get rttMs(): number {
    return this.rtt;
  }

  connect(): void {
    const t = this.factory(this.url);
    this.transport = t;
    t.onOpen = (): void => {
      // ClientHello is raw token bytes, already framed by the codec (welcome.ts).
      t.send(encodeClientHello(this.token));
    };
    t.onMessage = (bytes: Uint8Array): void => this.handle(bytes);
    t.onClose = (reason: string): void => this.events.onClose?.(reason);
    t.onError = (err: Error): void => this.events.onError?.(err);
  }

  close(): void {
    this.transport?.close();
    this.transport = null;
  }

  /**
   * Send a redundant batch of recent inputs (§2.3). The codec truncates to
   * MAX_INPUT_BATCH, so passing a longer window is harmless but wastes nothing.
   */
  sendInputs(batch: readonly InputCommand[]): void {
    if (!this.welcomed || batch.length === 0) return;
    beginFrame(this.writer, MessageType.InputBatch);
    encodeInputBatch(this.writer, batch);
    if (this.writer.overflow) return; // never ship a truncated frame
    this.transport?.send(this.writer.finishToBytes());
  }

  /** Ask for a keyframe — baseline loss recovery (§2.8). */
  requestFullSnapshot(): void {
    beginFrame(this.writer, MessageType.FullSnapshotRequest);
    // No payload. beginFrame wrote the type byte; finish flushes it.
    this.transport?.send(this.writer.finishToBytes());
  }

  /** Probe RTT. Ignored if a probe is already outstanding. */
  sendPing(nowMs: number): void {
    if (!this.open || this.pingInFlight !== 0) return;
    const nonce = this.pingNonce++ >>> 0;
    this.pingInFlight = nonce;
    this.pingSentAtMs = nowMs;
    beginFrame(this.writer, MessageType.Ping);
    this.writer.writeBits(nonce, PING_NONCE_BITS);
    this.transport?.send(this.writer.finishToBytes());
  }

  // ── Receive ───────────────────────────────────────────────────────────────

  private handle(bytes: Uint8Array): void {
    switch (peekPacketType(bytes)) {
      case MessageType.Welcome:
        this.handleWelcome(bytes);
        break;
      case MessageType.Snapshot:
        this.handleSnapshot(bytes);
        break;
      case MessageType.Pong:
        this.handlePong(bytes);
        break;
      case MessageType.ControlAssign:
        this.handleControlAssign(bytes);
        break;
      case MessageType.RefereeSignal:
        this.handleRefereeSignal(bytes);
        break;
      case MessageType.Reject:
        this.handleReject(bytes);
        break;
      default:
        // Unknown type: a version skew or a corrupt frame. Dropping is correct —
        // the version gate below is what actually resolves skew.
        break;
    }
  }

  private handleWelcome(bytes: Uint8Array): void {
    this.reader.reset(framePayload(bytes));
    const payload = decodeWelcome(this.reader);
    if (this.reader.overflow) {
      this.events.onError?.(new Error('malformed Welcome'));
      return;
    }
    // §12.6 / §15.2: a version mismatch causes silent desyncs that are hell to
    // debug. This is NOT just a protocol (wire) check any more — it also rejects a
    // server whose SIM differs even when the bytes line up: a different
    // simulationVersion, or a different constantsHash (any gameplay tunable in
    // constants.ts changed). gitCommit is ignored — differing commits with an
    // identical sim are allowed. In production a MISSING fingerprint is rejected
    // too (an old/unidentifiable server). Refuse to play rather than find out
    // three days of "the physics feels wrong" later.
    //
    // ── HMR note (§15.2) ──────────────────────────────────────────────────────
    // A Vite hot-update to any /sim module (e.g. editing a number in
    // constants.ts) changes this client's constantsHash. It does NOT retroactively
    // re-check an already-live connection — but the moment the session reconnects
    // (or a new match is joined) this gate runs against the server's OLD hash and
    // rejects. The safe workflow for a sim edit is a full reload, which re-runs the
    // handshake; the constantsHash mismatch is the runtime backstop that catches a
    // dev who forgot. See devtools/overlay.ts (onRequireOffline) for the same rule
    // applied to live tunable edits.
    const local = buildFingerprint();
    const server: BuildFingerprint = {
      gitCommit: payload.gitCommit,
      protocolVersion: payload.protocolVersion,
      simulationVersion: payload.simulationVersion,
      constantsHash: payload.constantsHash,
    };
    const reason = classifyFingerprintMismatch(local, server, {
      requireFingerprint: this.requireFingerprint,
    });
    if (reason !== null) {
      // Legacy callback kept for a pure protocol skew; onVersionMismatch is the
      // general, visible path every caller should handle.
      if (reason === 'protocol') {
        this.events.onProtocolMismatch?.(payload.protocolVersion, local.protocolVersion);
      }
      this.events.onVersionMismatch?.({
        reason,
        message: mismatchMessage(reason, local, server),
        local,
        server,
      });
      this.close();
      return;
    }
    this.welcomed = true;
    this.events.onWelcome?.(payload);
  }

  private handleSnapshot(bytes: Uint8Array): void {
    this.reader.reset(framePayload(bytes));
    const header = decodeSnapshot(this.reader, this.baseline, this.pending);
    if (this.reader.overflow) {
      // A truncated snapshot has already half-written `pending`; that frame is
      // garbage but harmless because we do not promote it to baseline. Ask for
      // a keyframe rather than trying to salvage it.
      this.requestFullSnapshot();
      return;
    }
    // The delta was built against a frame we do not hold — impossible on an
    // ordered transport, guaranteed to happen the day this becomes unreliable.
    // Applying it anyway would corrupt every field the delta considered
    // "unchanged", so recover with a keyframe instead.
    if (!header.isFull && header.baselineTick !== this.baseline.tick) {
      this.requestFullSnapshot();
      return;
    }

    // Promote: `pending` is now the complete frame this client holds. Swap
    // rather than copy — the old baseline becomes the next decode target.
    const prev = this.baseline;
    this.baseline = this.pending;
    this.pending = prev;

    this.lastProcessedInputSeq = header.lastProcessedInputSeq;
    this.events.onSnapshot?.(this.baseline, header);
  }

  private handlePong(bytes: Uint8Array): void {
    this.reader.reset(framePayload(bytes));
    const nonce = this.reader.readBits(PING_NONCE_BITS) >>> 0;
    if (this.reader.overflow || nonce !== this.pingInFlight) return;
    this.pingInFlight = 0;
    const sample = performance.now() - this.pingSentAtMs;
    // EMA. A single sample is dominated by whichever frame boundary it landed
    // on; the HUD wants a stable number, not an honest instantaneous one.
    this.rtt = this.rtt < 0 ? sample : this.rtt * 0.8 + sample * 0.2;
  }

  private handleControlAssign(bytes: Uint8Array): void {
    this.reader.reset(framePayload(bytes));
    const playerId = decodeControlAssign(this.reader);
    // -1 = truncated or an out-of-range id; ignore rather than rebase onto a
    // nonexistent player (decodeControlAssign already bounds it).
    if (playerId < 0) return;
    this.onControlAssign?.(playerId);
  }

  /**
   * §5.6 — the referee whistled a restart or showed a card. Purely a
   * presentation cue: it is not acked, not replayed, and not fed into
   * prediction, so a dropped or rejected frame costs a sound and a graphic and
   * never an outcome. `decodeRefSignal` rejects a truncated frame or an unknown
   * kind, which is why a bad packet is silently ignored here rather than
   * surfaced as an error — an unrecognised kind is exactly what a NEWER server
   * would send, and a missed cue must never close a live match.
   */
  private handleRefereeSignal(bytes: Uint8Array): void {
    this.reader.reset(framePayload(bytes));
    if (!decodeRefSignal(this.reader, this.refSignal)) return;
    this.events.onRefereeSignal?.(this.refSignal);
  }

  private handleReject(bytes: Uint8Array): void {
    const payload = framePayload(bytes);
    const reason = (payload.length > 0 ? payload[0]! : 2) as RejectReason;
    this.events.onReject?.(reason);
    this.close();
  }
}
